import React from 'react';
import {
  format,
  addDays,
  addMonths,
  subMonths,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  isToday,
  isSameMonth,
  Locale,
} from 'date-fns';

const headerFormat = 'MMMM yyyy';
const dayWeekFormat = 'eeeeee';
const dayFormat = 'd';

const hoursOptions: string[] = [];
for (let i = 0; i < 24; i++) {
  const minute = `0${i}`.slice(-2);
  hoursOptions.push(`${minute}:00`, `${minute}:30`);
}

type DayRow = {
  id: string;
  date: Date;
  formatted: string;
  isToday: boolean;
  isSameMonth: boolean;
};

type CalendarAction = 'forward' | 'back';

type UseCalendarOptions = {
  locale: Locale;
};

export type UseCalendarResult = {
  header: string;
  navigate: (action: CalendarAction) => void;
  getDaysOfWeek: () => string[];
  getDays: () => DayRow[][];
  hoursOptions: string[];
  selectedMonth: number
};

const useCalendar = ({ locale }: UseCalendarOptions): UseCalendarResult => {
  const [now, setNow] = React.useState(new Date());

  const header = format(now, headerFormat, { locale });

  const selectedMonth = now.getMonth() + 1
 
  const getDaysOfWeek = () => {
    let startDate = startOfWeek(now);
    return [...Array(7).keys()].map(index =>
      format(addDays(startDate, index), dayWeekFormat, { locale }),
    );
  };

  const getDays = () => {
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const days = [];
    let day = startDate;

    while (day <= endDate) {
      const row: DayRow[] = [];

      // eslint-disable-next-line no-loop-func
      [...Array(7)].forEach(() => {
        const formatted = format(day, dayFormat, { locale });
        row.push({
          id: formatted,
          date: day,
          formatted,
          isToday: isToday(day),
          isSameMonth: isSameMonth(day, monthStart),
        });

        day = addDays(day, 1);
      });

      days.push(row);
    }

    return days;
  };

  const navigate = (action: CalendarAction) => {
    if (action === 'forward') {
      setNow(addMonths(now, 1));
    }
    if (action === 'back') {
      setNow(subMonths(now, 1));
    }
  };

  return { header, navigate, getDaysOfWeek, getDays, hoursOptions, selectedMonth };
};

export default useCalendar;